import type {
  LeaveTypesMapperT,
  GetAllLeaveTypesResponseT,
  CreateLeaveTypeResponseT,
  UpdateLeaveTypeResponseT,
  DeleteLeaveTypesResponseT,
} from "../types/leaveTypes.types";
import type {
  LeaveTypeItemResponseDtoT,
  CreateJustifiedLeaveTypeRequestDtoT,
  CreateNonJustifiedLeaveTypeRequestDtoT,
  UpdateLeaveTypeRequestDtoT,
  DeleteLeaveTypesRequestDtoT,
  ToggleLeaveTypeActiveStatusRequestDtoT,
} from "../../domain/dtos/leaveType.dto";
import type { LeaveTypeEntityT } from "../../domain/entities/leaveType.entity";
import type {
  CreateLeaveTypeFormValuesT,
  UpdateLeaveTypeTypeFormValuesT,
  LeaveTypeMutationResponseT,
  DeleteLeaveTypesMutationResponseT,
} from "../../domain/types/leaveType.types";

const mapItemToDomain = (
  item: LeaveTypeItemResponseDtoT,
): LeaveTypeEntityT => ({
  id: item.id ?? "",
  code: item.code,
  name: item.name,
  description: item.description,
  color: item.color,
  icon: item.icon,
  isJustified: item.is_justified,
  active: item.active,
  isCustom: item.is_custom,
  incidence: item.incidence
    ? {
        id: item.incidence.id,
        code: item.incidence.code,
        name: item.incidence.name,
      }
    : null,
  policy: item.policy
    ? {
        id: item.policy.id ?? "",
        accrualMode: item.policy.accrual_mode,
        active: item.policy.active,
        allowNegativeBalance: item.policy.allow_negative_balance,
        annualDays: item.policy.annual_days,
        approvalFlow: item.policy.approval_flow,
        hasBalanceControl: item.policy.has_balance_control,
        maxAdvanceDays: item.policy.max_advance_days,
        maxBackdateDays: item.policy.max_backdate_days,
        maxDaysPerRequest: item.policy.max_days_per_request,
        maxNegativeBalanceDays: item.policy.max_negative_balance_days,
        minAdvanceNoticeDays: item.policy.min_advance_notice_days,
        minimumEligibilityDays: item.policy.minimum_eligibility_days,
        requiresAttachment: item.policy.requires_attachment,
        resetMonth: item.policy.reset_month,
      }
    : null,
});

export const leaveTypesMapper: LeaveTypesMapperT = {
  leaveTypesDtoToDomain: (
    rawData: GetAllLeaveTypesResponseT,
  ): LeaveTypeEntityT[] => {
    const data = rawData?.data ?? [];

    if (!Array.isArray(data) || data.length === 0) {
      return [];
    }

    return data.map(mapItemToDomain);
  },
  toCreateDTO: (
    formValues: CreateLeaveTypeFormValuesT,
  ):
    | CreateJustifiedLeaveTypeRequestDtoT
    | CreateNonJustifiedLeaveTypeRequestDtoT => {
    const base = {
      code: formValues.code,
      incidence_type_id: formValues.incidenceTypeId,
      is_justified: formValues.isJustified,
      name: formValues.name,
    };

    if (formValues.isJustified) {
      return { types: [base] };
    }

    const hasBalance = formValues.hasBalanceControl === "yes";
    const hasAdvanceNotice = formValues.advanceNoticeRequirement === "required";
    const hasBackdate = formValues.allowBackdateDays === "yes";

    return {
      types: [
        {
          ...base,
          icon: formValues.icon ?? "",
          color: formValues.color ?? null,
          description: formValues.description ?? null,
          policy: {
            annual_days: formValues.annualDays ?? null,
            approval_flow: formValues.approvalFlow ?? "",
            has_balance_control: hasBalance,
            max_advance_days: formValues.maxAdvanceDays ?? 0,
            max_backdate_days: hasBackdate
              ? (formValues.maxBackdateDays ?? null)
              : null,
            max_days_per_request: formValues.maxDaysPerRequest ?? null,
            min_advance_notice_days: hasAdvanceNotice
              ? (formValues.minAdvanceNoticeDays ?? null)
              : null,
            requires_attachment: formValues.requiresAttachment === "yes",
            reset_month: formValues.resetMonth ?? null,
          },
        },
      ],
    };
  },
  createDtoToDomain: (
    rawData: CreateLeaveTypeResponseT,
  ): LeaveTypeMutationResponseT => {
    const result = rawData?.data;

    if (!result) {
      throw new Error("No se pudo crear el tipo de permiso");
    }

    return {
      succeeded: (result.succeeded ?? []).map(mapItemToDomain),
      failed: (result.failed ?? []).map((item) => ({
        code: item.code,
        reason: item.reason,
        index: item.index,
      })),
    };
  },
  toDTO: (
    data: UpdateLeaveTypeTypeFormValuesT,
  ): UpdateLeaveTypeRequestDtoT => ({
    types: [
      {
        type_id: data.id,
        code: data.code,
        name: data.name,
        description: data.description ?? null,
        icon: data.icon,
        is_justified: data.isJustified,
        incidence_type_id: data.incidenceTypeId ?? null,
      },
    ],
  }),
  updateDtoToDomain: (
    rawData: UpdateLeaveTypeResponseT,
  ): LeaveTypeMutationResponseT => {
    const result = rawData?.data;

    if (!result) {
      throw new Error("Ocurrió un problema al actualizar el tipo de permiso");
    }

    return {
      succeeded: (result.succeeded ?? []).map(mapItemToDomain),
      failed: (result.failed ?? []).map((item) => ({
        code: item.code,
        reason: item.reason,
        index: item.index,
      })),
    };
  },
  toDeleteDTO: (typeIds: string[]): DeleteLeaveTypesRequestDtoT => ({
    types: typeIds,
  }),
  deleteDtoToDomain: (
    rawData: DeleteLeaveTypesResponseT,
  ): DeleteLeaveTypesMutationResponseT => {
    const result = rawData?.data;

    if (!result) {
      throw new Error("Ocurrió un problema al eliminar el tipo de permiso");
    }

    return {
      succeeded: result.succeeded ?? [],
      failed: (result.failed ?? []).map((item) => ({
        typeId: item.type_id,
        reason: item.reason,
        index: item.index,
      })),
    };
  },
  toToggleActiveStatusDTO: (
    isActive: boolean,
  ): ToggleLeaveTypeActiveStatusRequestDtoT => ({
    is_active: isActive,
  }),
};
